import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, HardHat, Truck, Building2, Ambulance, TreePine, Factory, Wifi, Network, Radio as RadioIcon, Wrench, Clock, Headphones, Lock, ShieldCheck, Smartphone, Layers, ListOrdered, Satellite, RefreshCcw, Database, Cpu, Server, Gauge, KeyRound, Globe2 } from "lucide-react";
import SEO from "@/components/SEO";

const SOLUTIONS = [
  { icon: <HardHat size={22} />, name: "Construction & Civil", text: "Reliable site comms across remote builds, with PTT radios and rugged tablets that survive the job." },
  { icon: <Truck size={22} />, name: "Transport & Logistics", text: "Fleet-wide PTT, GPS and vehicle telemetry over multi-network SIMs that don't drop at the depot fence." },
  { icon: <Ambulance size={22} />, name: "First Response & Security", text: "Evidence-grade body cams, encrypted PTT and live streaming for frontline teams." },
  { icon: <Building2 size={22} />, name: "Utilities & Infrastructure", text: "Mission-critical comms for field crews, with broadband redundancy for control rooms." },
  { icon: <TreePine size={22} />, name: "Forestry & Primary Industries", text: "Coverage where one carrier won't cut it. Tri-network SIMs keep your team in range." },
  { icon: <Factory size={22} />, name: "Manufacturing & Warehousing", text: "PoC handsets, ATEX-rated radios for hazardous zones and PDAs for inventory ops." },
];

const Solutions = () => (
  <div data-testid="solutions-page">
    <SEO
      path="/solutions"
      title="Multi-Network Solutions — APN, MDM, BYOC & IoT"
      description="Multi-network SIM failover, Private APN & VPN, Android MDM and BYOC eSIM import — connectivity solutions for utilities, government and critical infrastructure."
      keywords="mission critical communications New Zealand, mission critical communications Australia, utilities communication NZ, construction site comms Australia, transport logistics fleet SIM NZ AU, government network resilience NZ AU, forestry communications New Zealand, manufacturing warehouse comms, network redundancy business NZ, private APN New Zealand, private APN Australia, secure VPN mobile network business, dedicated APN business SIM NZ AU, isolated cellular network enterprise, secure mobile connectivity government NZ"
    />
    <section className="bg-[var(--ptt-paper)] border-b border-[var(--ptt-line)]">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 pt-16 pb-14">
        <div className="eyebrow">Solutions</div>
        <h1 className="font-display text-5xl lg:text-6xl font-bold mt-3 leading-[1.02] tracking-tight max-w-3xl">
          Comms that match the job — <span className="text-[var(--ptt-teal-deep)]">not the other way around.</span>
        </h1>
        <p className="text-[var(--ptt-muted)] mt-5 max-w-2xl">From frontline first-response to forestry crews working past cellular fringes, we build comms stacks around the way your teams actually operate.</p>
      </div>
    </section>

    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="solutions-grid">
        {SOLUTIONS.map((s) => (
          <div key={s.name} className="group bg-white border border-[var(--ptt-line)] p-6 hover:border-[var(--ptt-ink)] transition-colors" data-testid={`solution-${s.name.replace(/\s+/g, '-').toLowerCase()}`}>
            <div className="w-11 h-11 grid place-items-center bg-[var(--ptt-teal)]/15">{s.icon}</div>
            <h3 className="font-display text-xl font-bold mt-5">{s.name}</h3>
            <p className="text-sm text-[var(--ptt-muted)] mt-2 leading-relaxed">{s.text}</p>
          </div>
        ))}
      </div>
    </section>

    {/* Broadband redundancy block */}
    <section className="bg-[var(--ptt-ink)] text-white">
      <div className="max-w-7xl mx-auto px-5 lg:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
        <div className="lg:col-span-7">
          <div className="eyebrow text-white/50">Connectivity</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">
            Broadband redundancy &<br /> fibre solutions for <span className="text-[var(--ptt-teal)]">always-on</span> sites.
          </h2>
          <p className="mt-5 text-white/75 max-w-xl">Pair our global multi-network SIM/eSIM platform with managed fibre broadband for a connectivity stack that fails over gracefully — keeping your control room, retail site or warehouse on the air.</p>
          <div className="mt-7 flex gap-3 flex-wrap">
            <Link to="/contact" data-testid="solutions-cta-contact" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Discuss your deployment <ArrowRight size={14} /></Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          <div className="border border-white/15 p-6">
            <div className="flex items-center gap-2.5 mb-4">
              <Wifi size={18} className="text-[var(--ptt-teal)]" />
              <span className="font-mono-tech text-[11px] tracking-widest uppercase text-white/70">Redundancy stack</span>
            </div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3"><span className="font-mono-tech text-[11px] text-[var(--ptt-teal)] mt-0.5">01</span>Primary fibre broadband</li>
              <li className="flex items-start gap-3"><span className="font-mono-tech text-[11px] text-[var(--ptt-teal)] mt-0.5">02</span>Multi-network SIM failover — strongest signal wins (NZ + AU + global)</li>
              <li className="flex items-start gap-3"><span className="font-mono-tech text-[11px] text-[var(--ptt-teal)] mt-0.5">03</span>Automatic, no-config handover</li>
              <li className="flex items-start gap-3"><span className="font-mono-tech text-[11px] text-[var(--ptt-teal)] mt-0.5">04</span>NZ-based monitoring & support</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* PRIVATE APN & SECURE VPN — teaser */}
    <section id="private-apn" className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24">
      <div className="border border-[var(--ptt-ink)] bg-white p-8 lg:p-14 relative overflow-hidden">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[var(--ptt-teal)]/15 blur-3xl" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow">Enterprise-grade security</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3 leading-[1.05] max-w-2xl">
              When the public internet <span className="text-[var(--ptt-teal-deep)]">isn&apos;t an option.</span>
            </h2>
            <p className="mt-5 text-[var(--ptt-muted)] max-w-2xl leading-relaxed">
              For deployments that can&apos;t risk exposure to the open internet, PTT NZ builds custom Private APN, static/dynamic IP and IPSec VPN connectivity on top of our multi-network platform — engineered and priced around your compliance and security requirements.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="private-apn-teaser-badges">
              {[
                { icon: <Lock size={13} />, label: "Private APN" },
                { icon: <ShieldCheck size={13} />, label: "Secure VPN" },
                { icon: <Network size={13} />, label: "Static & dynamic IP" },
              ].map((b) => (
                <span key={b.label} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-[var(--ptt-line)] text-[var(--ptt-muted)] inline-flex items-center gap-1.5">{b.icon}{b.label}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/solutions/private-apn" data-testid="private-apn-teaser-cta" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Explore Private APN &amp; VPN <ArrowRight size={16} /></Link>
          </div>
        </div>
      </div>
    </section>

    {/* ANDROID MDM — teaser */}
    <section id="android-mdm" className="max-w-7xl mx-auto px-5 lg:px-10 py-0 lg:py-0">
      <div className="border border-[var(--ptt-ink)] bg-white p-8 lg:p-14 relative overflow-hidden">
        <div className="absolute -left-16 -bottom-16 w-72 h-72 rounded-full bg-[var(--ptt-teal)]/15 blur-3xl" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow">Native platform capability</div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3 leading-[1.05] max-w-2xl">
              Android MDM, <span className="text-[var(--ptt-teal-deep)]">built right into your portal.</span>
            </h2>
            <p className="mt-5 text-[var(--ptt-muted)] max-w-2xl leading-relaxed">
              Every PTT NZ account already includes native Android Enterprise device management — zero-touch enrollment, remote lock &amp; wipe, custom APN loading, kiosk mode, app control and GPS tracking, all from the same portal you use for SIMs and billing.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="mdm-teaser-badges">
              {[
                { icon: <Smartphone size={13} />, label: "Zero-touch enrollment" },
                { icon: <Lock size={13} />, label: "Remote lock & wipe" },
                { icon: <Wifi size={13} />, label: "Custom APN loading" },
              ].map((b) => (
                <span key={b.label} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-[var(--ptt-line)] text-[var(--ptt-muted)] inline-flex items-center gap-1.5">{b.icon}{b.label}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/solutions/android-mdm" data-testid="mdm-teaser-cta" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center">Explore Android MDM <ArrowRight size={16} /></Link>
          </div>
        </div>
      </div>
    </section>

    {/* BYOC — teaser */}
    <section id="byoc" className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-24">
      <div className="group border border-[var(--ptt-ink)] bg-white p-8 lg:p-14 relative overflow-hidden hover:shadow-[8px_8px_0_0_var(--ptt-teal)] transition-shadow duration-300">
        <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-[var(--ptt-teal)]/15 blur-3xl group-hover:bg-[var(--ptt-teal)]/25 transition-colors duration-300" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--ptt-teal)] signal-dot" />
              Bring Your Own Carrier
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3 leading-[1.05] max-w-2xl">
              Keep your carrier plan. <span className="text-[var(--ptt-teal-deep)]">Add a network that never lets you go dark.</span>
            </h2>
            <p className="mt-5 text-[var(--ptt-muted)] max-w-2xl leading-relaxed">
              One Smart SIM, 8+ carrier profiles deep. Your plan stays active by default, PTT NZ's multi-network carriers step in the instant it drops data — and it's the easiest way to migrate a whole fleet to a new plan over the air, with zero downtime and zero site visits.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="byoc-teaser-badges">
              {[
                { icon: <Layers size={13} />, label: "8+ profiles per Smart SIM" },
                { icon: <ListOrdered size={13} />, label: "Rule or signal-based failover" },
                { icon: <RefreshCcw size={13} />, label: "Zero-downtime fleet migration" },
                { icon: <Satellite size={13} />, label: "Starlink Direct to Cell ready" },
              ].map((b) => (
                <span key={b.label} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-[var(--ptt-line)] text-[var(--ptt-muted)] inline-flex items-center gap-1.5">{b.icon}{b.label}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/solutions/byoc" data-testid="byoc-teaser-cta" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center group-hover:gap-3.5 transition-all">See how BYOC failover works <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></Link>
          </div>
        </div>
      </div>
    </section>

    {/* IoT & Data Pools — teaser */}
    <section id="iot-data-pools" className="max-w-7xl mx-auto px-5 lg:px-10 pb-20 lg:pb-24">
      <div className="group border border-[var(--ptt-ink)] bg-white p-8 lg:p-14 relative overflow-hidden hover:shadow-[8px_8px_0_0_var(--ptt-teal)] transition-shadow duration-300">
        <div className="absolute -right-16 -bottom-16 w-72 h-72 rounded-full bg-[var(--ptt-teal)]/15 blur-3xl group-hover:bg-[var(--ptt-teal)]/25 transition-colors duration-300" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--ptt-teal)] signal-dot" />
              IoT &amp; Data Pools
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3 leading-[1.05] max-w-2xl">
              Scalable IoT data pools, <span className="text-[var(--ptt-teal-deep)]">built for device monitoring &amp; infrastructure.</span>
            </h2>
            <p className="mt-5 text-[var(--ptt-muted)] max-w-2xl leading-relaxed">
              Centralise, streamline and scale with intelligent multi-network connectivity — per-SIM IoT plans or a shared Data Pool across your whole fleet of sensors, cameras and controllers.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="iot-teaser-badges">
              {[
                { icon: <Cpu size={13} />, label: "Per-SIM or pooled data" },
                { icon: <Database size={13} />, label: "Centralised fleet billing" },
                { icon: <RefreshCcw size={13} />, label: "Scale up or down over the air" },
              ].map((b) => (
                <span key={b.label} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-[var(--ptt-line)] text-[var(--ptt-muted)] inline-flex items-center gap-1.5">{b.icon}{b.label}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/iot-data-pools" data-testid="iot-teaser-cta" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center group-hover:gap-3.5 transition-all">Explore IoT &amp; Data Pools <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></Link>
          </div>
        </div>
      </div>
    </section>

    {/* Enterprise IoT — teaser */}
    <section id="enterprise-iot" className="max-w-7xl mx-auto px-5 lg:px-10 pb-20 lg:pb-24">
      <div className="group border border-[var(--ptt-ink)] bg-white p-8 lg:p-14 relative overflow-hidden hover:shadow-[8px_8px_0_0_var(--ptt-teal)] transition-shadow duration-300">
        <div className="absolute -left-16 -bottom-16 w-72 h-72 rounded-full bg-[var(--ptt-teal)]/15 blur-3xl group-hover:bg-[var(--ptt-teal)]/25 transition-colors duration-300" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--ptt-teal)] signal-dot" />
              Enterprise & Industrial IoT
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3 leading-[1.05] max-w-2xl">
              For network engineers, <span className="text-[var(--ptt-teal-deep)]">not just procurement.</span>
            </h2>
            <p className="mt-5 text-[var(--ptt-muted)] max-w-2xl leading-relaxed">
              A non-steered independent core network, native Local Breakouts in Auckland and Sydney, and hardware-level LTE-M failover — engineered for utilities, SCADA and fleets where uptime isn&apos;t negotiable.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="eiot-teaser-badges">
              {[
                { icon: <Server size={13} />, label: "Non-steered core" },
                { icon: <Gauge size={13} />, label: "<40ms Local Breakout latency" },
                { icon: <Cpu size={13} />, label: "LTE-M hardware failover" },
              ].map((b) => (
                <span key={b.label} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-[var(--ptt-line)] text-[var(--ptt-muted)] inline-flex items-center gap-1.5">{b.icon}{b.label}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/solutions/enterprise-iot" data-testid="eiot-teaser-cta" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center group-hover:gap-3.5 transition-all">Explore Enterprise IoT <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></Link>
          </div>
        </div>
      </div>
    </section>

    {/* Government & Public Safety — teaser */}
    <section id="government" className="max-w-7xl mx-auto px-5 lg:px-10 pb-20 lg:pb-24">
      <div className="group border border-[var(--ptt-ink)] bg-white p-8 lg:p-14 relative overflow-hidden hover:shadow-[8px_8px_0_0_var(--ptt-teal)] transition-shadow duration-300">
        <div className="absolute -right-16 -top-16 w-72 h-72 rounded-full bg-[var(--ptt-teal)]/15 blur-3xl group-hover:bg-[var(--ptt-teal)]/25 transition-colors duration-300" />
        <div className="relative grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <div className="eyebrow flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[var(--ptt-teal)] signal-dot" />
              Government &amp; Public Safety
            </div>
            <h2 className="font-display text-3xl lg:text-4xl font-bold mt-3 leading-[1.05] max-w-2xl">
              Your primary network, <span className="text-[var(--ptt-teal-deep)]">or a resilience layer alongside it.</span>
            </h2>
            <p className="mt-5 text-[var(--ptt-muted)] max-w-2xl leading-relaxed">
              Full tri-network cellular access, deployable as a complete primary network or as a complementary layer alongside an existing one — with local breakouts in Auckland and Sydney, domestically hosted management with mandatory 2FA, and zero-firmware eUICC provisioning either way.
            </p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="gov-teaser-badges">
              {[
                { icon: <Globe2 size={13} />, label: "Local breakouts, local sovereignty" },
                { icon: <KeyRound size={13} />, label: "NZ-hosted, mandatory 2FA" },
                { icon: <ShieldCheck size={13} />, label: "Zero-firmware eUICC provisioning" },
              ].map((b) => (
                <span key={b.label} className="font-mono-tech text-[10px] uppercase tracking-widest px-2.5 py-1.5 border border-[var(--ptt-line)] text-[var(--ptt-muted)] inline-flex items-center gap-1.5">{b.icon}{b.label}</span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 lg:items-end">
            <Link to="/government-public-safety" data-testid="gov-teaser-cta" className="ptt-button px-6 py-3.5 text-sm font-bold inline-flex items-center gap-2 w-full sm:w-auto justify-center group-hover:gap-3.5 transition-all">Explore Government &amp; Public Safety <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" /></Link>
          </div>
        </div>
      </div>
    </section>

    {/* NETWORK DESIGN & BUILD */}
    <section className="max-w-7xl mx-auto px-5 lg:px-10 py-20 lg:py-28">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div className="eyebrow">Network design & build</div>
          <h2 className="font-display text-4xl lg:text-5xl font-bold mt-3 leading-[1.05]">
            Your complete <span className="text-[var(--ptt-teal-deep)]">connectivity</span> partner.
          </h2>
          <p className="mt-5 text-[var(--ptt-muted)] leading-relaxed">
            Beyond hardware, PTT NZ delivers full network design and configuration — wireless site-to-site links, WISP services, and Wi-Fi deployments for small businesses right through to large-scale warehouses and logistics firms. One vendor, one phone number, one bill.
          </p>
          <div className="mt-7 grid grid-cols-2 gap-3">
            <div className="border border-[var(--ptt-line)] bg-white px-4 py-3">
              <div className="flex items-center gap-2"><Clock size={14} className="text-[var(--ptt-teal-deep)]" /><div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Response</div></div>
              <div className="font-display text-2xl font-bold mt-1">≤ 3 hrs</div>
              <div className="text-[11px] text-[var(--ptt-muted)] mt-0.5">to every new ticket</div>
            </div>
            <div className="border border-[var(--ptt-line)] bg-white px-4 py-3">
              <div className="flex items-center gap-2"><Headphones size={14} className="text-[var(--ptt-teal-deep)]" /><div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Support</div></div>
              <div className="font-display text-2xl font-bold mt-1">24 / 7</div>
              <div className="text-[11px] text-[var(--ptt-muted)] mt-0.5">NZ-based team</div>
            </div>
          </div>
          <div className="mt-6">
            <Link to="/contact" data-testid="network-cta-contact" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2">Scope a network build <ArrowRight size={14} /></Link>
          </div>
        </div>

        <div className="lg:col-span-7">
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { icon: <Network size={20} />, title: "Business fibre broadband", text: "A range of fibre plans tailored to NZ businesses — competitively priced, with managed routers and proactive monitoring." },
              { icon: <Wifi size={20} />, title: "Wi-Fi deployment", text: "From cafés to 20,000 m² warehouses — seamless roaming, captive portals, segmented guest/staff networks." },
              { icon: <RadioIcon size={20} />, title: "Wireless site-to-site links", text: "PtP and PtMP links engineered for line-of-sight challenges across NZ terrain. Bonded paths for resilience." },
              { icon: <Wrench size={20} />, title: "WISP services", text: "Wireless Internet for hard-to-reach sites where fibre or cellular alone isn't enough." },
            ].map((b) => (
              <div key={b.title} className="bg-white border border-[var(--ptt-line)] p-5 hover:border-[var(--ptt-ink)] transition-colors" data-testid={`net-card-${b.title.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="w-10 h-10 grid place-items-center bg-[var(--ptt-teal)]/15">{b.icon}</div>
                <h3 className="font-display text-base font-bold mt-4">{b.title}</h3>
                <p className="text-xs text-[var(--ptt-muted)] mt-1.5 leading-relaxed">{b.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-6 border border-[var(--ptt-line)] bg-white p-5">
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">Hardware we engineer with</div>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="hardware-brands">
              {["MikroTik", "Ubiquiti", "Peplink", "Cambium", "Ruckus", "TP-Link Omada", "Cisco Meraki"].map((b) => (
                <span key={b} className="px-3 py-1.5 border border-[var(--ptt-line)] text-xs font-display font-semibold">{b}</span>
              ))}
            </div>
            <p className="text-xs text-[var(--ptt-muted)] mt-3">Vendor-agnostic — we pick the right kit for the job, not the brand we&apos;re tied to.</p>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default Solutions;
